/**
 * View component for /user/login
 *
 * On render cycle this component checks to see if the redirectToReferrer boolean
 * is true (flipped on successful login).  If true, send the user back to the
 * referring page.  If false, show user login form.
 *
 * NOTE: upon reaching this page, user can toggle between /user/login and
 * /user/register without changing the original referring source route.
 */

// import primary libraries
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Redirect from 'react-router-dom/Redirect'
import withRouter from 'react-router-dom/withRouter'

// import actions actions
import * as firmActions from '../../firm/firmActions.js'
import * as userActions from '../userActions.js'
import * as activityActions from '../../activity/activityActions.js'

// import form components
import AlertModal from '../../../global/components/modals/AlertModal.jsx'
import Binder from '../../../global/components/Binder.jsx'
import brandingName from '../../../global/enum/brandingName.jsx'

// import user components
import UserLayout from '../components/UserLayout.jsx'
import UserLoginForm from '../components/UserLoginForm.jsx'
import UserTermsServicesModal from '../components/UserTermsServicesModal.jsx'
import UserQRCodeModal from '../components/UserQRCodeModal.jsx'

// UI Switcher
import UISwitchChecker from '../../../global/practice/components/PracticeUISwitcher.jsx'

import { Helmet } from 'react-helmet-async'
import { Button } from '../../../global/components/ui/button.jsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../global/components/ui/dialog.jsx'

class UserLogin extends Binder {
  constructor(props) {
    super(props)
    this.state = {
      errorMessage: '',
      errorCode: '',
      isErrorModalOpen: false,
      magicLinkSent: false,
      redirectToReferrer: false,
      user: {
        username: '',
        password: '',
        emailError: null,
      },
      userTermsServiceModalOpen: false,
      msalInstance: null,
      userQRCodeModalOpen: false,
      verifyErrorMessage: '',
      passwordResetToken: '',
    }
    this._bind(
      '_handleFormChange',
      '_handleFormSubmit',
      '_goToResetPass',
      '_sendMagicLink',
      '_nextToLogin',
      '_handleVerifyToken',
      '_finishLogin',
    )
  }

  componentDidMount() {
    const { dispatch } = this.props
    localStorage.clear()
    dispatch(firmActions.fetchSingleFirmByDomain())
  }

  _handleFormChange(e) {
    var nextState = this.state.user
    nextState[e.target.name] = e.target.value
    this.setState(nextState)
  }

  _nextToLogin() {
    const { history } = this.props

    history.push('/user/reset-password/' + this.state.passwordResetToken)
  }

  _handleFormSubmit(e) {
    e.preventDefault()
    const { dispatch } = this.props
    if (this.state.user.emailError) {
      return
    }

    dispatch(
      userActions.sendLogin(this.state.username, this.state.password),
    ).then(action => {
      if (action.success) {
        if (action.requires2FA) {
          // Not logged in yet: the server wants the authenticator code.
          this.setState({
            userQRCodeModalOpen: true,
            verifyErrorMessage: '',
          })
        } else {
          this._finishLogin(action)
        }
      } else {
        const currUser = action.user ? action.user : {}
        const rawMessage =
          action.message || action.error || 'An error occurred.'
        const errorMessage = rawMessage.replace(
          /^(Internal error|Validation error): /,
          '',
        )
        if (action.error != 'user-first-login') {
          this.setState({
            errorMessage,
            errorCode: action.errorCode || '',
            isErrorModalOpen: true,
          })
        } else {
          this.setState(
            { passwordResetToken: action.passwordResetToken },
            () => {
              if (currUser.isStaff) {
                this.setState({
                  userTermsServiceModalOpen: true,
                })
              } else {
                this._nextToLogin()
              }
            },
          )
        }
      }
    })
  }

  _sendMagicLink() {
    const { dispatch } = this.props
    dispatch(userActions.sendMagicLink(this.state.username)).then(action => {
      if (action.success) {
        this.setState({ magicLinkSent: true })
      } else {
        this.setState({
          errorMessage: action.error,
          isErrorModalOpen: true,
        })
      }
    })
  }

  _goToResetPass() {
    const { history } = this.props
    console.log('go to reset password')
    history.push('/user/forgot-password')
  }

  /**
   * The user is logged in (password only, or password + code). Decide where
   * they go: a billing redirect, the forced 2FA enrollment page, the page they
   * came from, or the dashboard.
   */
  _finishLogin(action) {
    const { dispatch, history, location } = this.props

    if (action.redirect_url) {
      if (action.logout) {
        dispatch(userActions.sendLogout()).then(() => {
          window.location = action.redirect_url
        })
      } else {
        window.location = action.redirect_url
      }
      return
    }

    if (action.requires2FAEnrollment || action.force_2fa) {
      // Enrollment-only session: the server refuses everything else until 2FA
      // is enabled, and YTRoute keeps the user on this page.
      history.push({
        pathname: '/user/force_2fa',
      })
      return
    }

    dispatch(activityActions.sendCreateLoggedInUserActivity())

    if (location.state && location.state.from) {
      this.setState({ redirectToReferrer: true })
    } else {
      history.push({
        pathname: '/',
        search: window.location.search,
      })
    }
  }

  _handleVerifyToken(token) {
    const { dispatch } = this.props

    dispatch(userActions.sendVerify2FAToken(token)).then(action => {
      if (action.success) {
        this.setState({ userQRCodeModalOpen: false, verifyErrorMessage: '' })
        this._finishLogin(action)
      } else if (action.errorCode === 'MFA_ATTEMPTS_EXCEEDED') {
        // The pending login was dropped server-side; back to the password.
        this.setState({
          userQRCodeModalOpen: false,
          verifyErrorMessage: '',
          errorMessage:
            action.error ||
            'Too many incorrect verification codes. Please sign in again.',
          errorCode: action.errorCode,
          isErrorModalOpen: true,
        })
      } else {
        this.setState({
          verifyErrorMessage: action.error || 'Invalid verification code.',
        })
      }
    })
  }

  render() {
    const { firmStore } = this.props
    const { from } = this.props.location.state || { from: { pathname: '/' } }
    const { redirectToReferrer, user } = this.state

    const selectedFirm = firmStore.selected.getItem()

    const firmEmpty =
      !selectedFirm || !selectedFirm._id || firmStore.selected.didInvalidate

    const firmFetching = firmStore.selected.isFetching

    const logoUrl =
      selectedFirm &&
      selectedFirm._id &&
      selectedFirm.logoUrl &&
      `/api/firms/logo/${selectedFirm._id}/${selectedFirm.logoUrl}`
    const firmLogo = brandingName.image.logoBlack
    const isLockedOut =
      this.state.errorCode === 'ACCOUNT_LOCKED' ||
      this.state.errorCode === 'RATE_LIMITED' ||
      this.state.errorCode === 'MFA_ATTEMPTS_EXCEEDED'

    if (redirectToReferrer) {
      return <Redirect to={from} />
    } else {
      return (
        <UserLayout>
          <UISwitchChecker />
          <Helmet>
            <title>
              Login to {selectedFirm ? selectedFirm.name : brandingName.title}
            </title>
          </Helmet>
          <div className="yt-container">
            <div className="yt-row center-horiz">
              {firmEmpty ? (
                firmFetching ? (
                  <div className="-loading-hero hero">
                    <div className="u-centerText">
                      <div className="loading"></div>
                    </div>
                  </div>
                ) : (
                  <UserLoginForm
                    user={user}
                    handleFormSubmit={this._handleFormSubmit}
                    handleFormChange={this._handleFormChange}
                    location={this.props.location}
                    logoUrl={firmLogo}
                    magicLinkSent={this.state.magicLinkSent}
                    sendMagicLink={this._sendMagicLink}
                    selectedFirm={selectedFirm}
                  />
                )
              ) : (
                <UserLoginForm
                  user={user}
                  handleFormSubmit={this._handleFormSubmit}
                  handleFormChange={this._handleFormChange}
                  location={this.props.location}
                  logoUrl={logoUrl || firmLogo}
                  magicLinkSent={this.state.magicLinkSent}
                  sendMagicLink={this._sendMagicLink}
                  showPoweredBy={true}
                  handleMSAuthCallback={this._handleMSAuthCallback}
                  selectedFirm={selectedFirm}
                />
              )}
            </div>
            <Dialog
              open={this.state.isErrorModalOpen}
              onOpenChange={isOpen => {
                if (!isOpen) this.setState({ isErrorModalOpen: false })
              }}
            >
              <DialogContent className="sm:tw-max-w-md">
                <DialogHeader>
                  <DialogTitle>Error with sign in</DialogTitle>
                  <DialogDescription>
                    {this.state.errorMessage}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  {!isLockedOut && (
                    <Button
                      variant="outline"
                      onClick={this._goToResetPass}
                    >
                      Reset Password
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    onClick={() => this.setState({ isErrorModalOpen: false })}
                  >
                    {isLockedOut ? 'OK' : 'Try again'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <UserTermsServicesModal
              close={() => this.setState({ userTermsServiceModalOpen: false })}
              isOpen={this.state.userTermsServiceModalOpen}
              acceptTermsService={this._nextToLogin}
            />
            <UserQRCodeModal
              close={() =>
                this.setState({
                  userQRCodeModalOpen: false,
                  verifyErrorMessage: '',
                })
              }
              isOpen={this.state.userQRCodeModalOpen}
              acceptTermsService={() => {}}
              handleVerifyToken={this._handleVerifyToken}
              errorMessage={this.state.verifyErrorMessage}
            />
          </div>
        </UserLayout>
      )
    }
  }
}

UserLogin.propTypes = {
  dispatch: PropTypes.func.isRequired,
}

const mapStoreToProps = store => {
  return {
    firmStore: store.firm,
  }
}

export default withRouter(connect(mapStoreToProps)(UserLogin))
